import React, { useState } from 'react';
import { Check, ChevronsUpDown, Search, Plus } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

/**
 * SearchableItemSelect - A searchable dropdown for selecting items
 * 
 * @param {Object} props
 * @param {Array} props.items - Array of items [{id, name, code?, category_name?}]
 * @param {string} props.value - Currently selected item ID
 * @param {Function} props.onSelect - Callback when item is selected (id) => void
 * @param {Function} props.onCreateNew - Callback when "Create New" is clicked () => void
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.testId - Data test ID for testing
 * @param {boolean} props.showCreateNew - Whether to show the "Create New" option
 */
export const SearchableItemSelect = ({
  items = [],
  value,
  onSelect,
  onCreateNew,
  placeholder = "Select item...",
  className,
  disabled = false,
  testId,
  showCreateNew = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedItem = items.find(item => item.id === value);

  // Filter items based on search query
  const filteredItems = items.filter(item => {
    const query = searchQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.code?.toLowerCase().includes(query) ||
      item.category_name?.toLowerCase().includes(query)
    );
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled}
          data-testid={testId}
        >
          <span className="truncate">
            {selectedItem ? (
              <span className="flex items-center gap-2">
                <span>{selectedItem.name}</span>
                {selectedItem.code && (
                  <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                    {selectedItem.code}
                  </span>
                )}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search items by name or code..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {showCreateNew && onCreateNew && (
              <>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setOpen(false);
                      setSearchQuery('');
                      onCreateNew();
                    }}
                    className="cursor-pointer text-primary"
                    data-testid="create-new-item-option"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span className="font-medium">Create New Item</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandEmpty>
              <div className="py-2 text-center">
                <p className="text-sm text-muted-foreground">No items found.</p>
                {showCreateNew && onCreateNew && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-1"
                    onClick={() => {
                      setOpen(false);
                      setSearchQuery('');
                      onCreateNew();
                    }}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Create New Item
                  </Button>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {filteredItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => {
                    onSelect(item.id === value ? "" : item.id);
                    setOpen(false);
                    setSearchQuery('');
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === item.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{item.name}</span>
                      {item.code && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                          {item.code}
                        </span>
                      )}
                    </div>
                    {item.category_name && (
                      <span className="text-xs text-muted-foreground truncate">
                        {item.category_name}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableItemSelect;
